import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

/**
 * Gates where an underwriting agent (BSCPF-6141) can be added or attached:
 * the Underwriting agent card in Add AI Agent and the screening template's
 * Underwriting agent section. An agent that already exists keeps its row in
 * Users and AI agents and its page, so it can still be turned off or deleted.
 *
 * Both keys arrive in the partner's `features` array:
 * - `ai_underwriting_agent` is the entitlement. Without it the API leaves
 *   underwriting agents out of the agents list and refuses to create, read or
 *   change one, so the UI would only show errors.
 * - `ui_underwriting_agent` is a `ui_` registry flag (Boom::UiFeatureFlag),
 *   toggled per partner from admin's /ui-feature-flags page, so the screens can
 *   ship before the whole feature is done.
 *
 * A partner that has not loaded yet counts as OFF.
 *
 * ── REMOVING THE UI FLAG ──────────────────────────────────────────────────
 * Drop `UI_UNDERWRITING_AGENT` from `FEATURES` and the check on it below.
 */
export const useUnderwritingAgentsEnabled = () => {
  const { partner } = useAuthContext();

  return Boolean(
    partner?.features.includes(FEATURES.AI_UNDERWRITING_AGENT) &&
    partner.features.includes(FEATURES.UI_UNDERWRITING_AGENT)
  );
};

/**
 * Creating, changing or deleting an underwriting agent and its rules needs
 * screening-template access, the same check the API makes
 * (`authorize_screening_template_access!`).
 */
export const useCanManageUnderwritingAgents = () => {
  const { permissions } = useAuthContext();

  return (
    useUnderwritingAgentsEnabled() && permissions.canAccessScreeningTemplates
  );
};
